import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { languages, type Languages } from '../i18n/i18n.language-config';

import {
  NaicsIndustryAutoComplete,
  type NaicsIndustryAutoCompleteProps,
} from './NaicsIndustryAutoComplete';
import { SicIndustryAutoComplete } from './SicIndustryAutoComplete';

interface Props extends NaicsIndustryAutoCompleteProps {
  language?: string;
}

export type IndustryValue = {
  label: string;
  value: string;
  meta: {
    primaryIndustry: string;
    secondaryIndustry: string;
    naicsCode: string;
    industryClass?: number;
    sicCode?: string;
    sicPrimaryDesc?: string;
    sicSecondaryDesc?: string;
  };
};

export default function LanguageIndustryAutoComplete({
  language,
  onSelect,
  ...props
}: Props) {
  const { i18n } = useTranslation();
  const formContext = useFormContext();

  const lang = (language || i18n.language) as Languages;
  const usesSicClassification =
    lang === languages['en-GB'] || lang === languages['en-AU'];

  const handleSelect = (value: IndustryValue) => {
    if (!formContext) {
      return onSelect(value);
    }

    onSelect(value);

    if (usesSicClassification) {
      formContext.setValue('sicCode', value?.meta?.sicCode, {
        shouldDirty: true,
      });
      formContext.setValue('sicPrimaryDesc', value?.meta?.sicPrimaryDesc, {
        shouldDirty: true,
      });
      formContext.setValue('sicSecondaryDesc', value?.meta?.sicSecondaryDesc, {
        shouldDirty: true,
      });
    }

    formContext.setValue('industryClass', value?.meta?.industryClass, {
      shouldDirty: true,
    });
  };

  if (usesSicClassification) {
    return <SicIndustryAutoComplete onSelect={handleSelect} {...props} />;
  }

  return <NaicsIndustryAutoComplete onSelect={handleSelect} {...props} />;
}

export const deriveIndustryCode = (
  formData: { naicsCode?: string; naics?: string; industryCode?: string },
  language: Languages
) => {
  const usesSicClassification =
    language === languages['en-GB'] || language === languages['en-AU'];
  if (usesSicClassification) {
    return {
      sicCode: formData?.industryCode,
      naicsCode: formData.naicsCode,
    };
  }

  return {
    naicsCode: formData?.naicsCode || formData?.naics,
  };
};
